import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form'
import { useHideDirtyState } from 'src/Libs/flow/advanced-settings/providers'
import { makeStyles } from 'tss-react/mui'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

interface WeekdayButtonInputProps<TFieldValues extends FieldValues> {
  name: Path<TFieldValues>
  text: string
  disabled?: boolean
  hideDirty: boolean
}

/**
 * Round button. Used for weekdays. "Mon", "Tue"...
 */
export const WeekdayButtonInput = <TFieldValues extends FieldValues>({
  name,
  text,
  disabled,
  hideDirty,
}: WeekdayButtonInputProps<TFieldValues>): JSX.Element => {
  const hideDirtyState = useHideDirtyState()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { invalid, isDirty } }) => (
        <WeekdayButton
          onChange={checked => field.onChange(checked)}
          checked={field.value}
          disabled={disabled || !canEdit}
          error={invalid}
          isDirty={isDirty && !hideDirtyState && !hideDirty}
        >
          {text}
        </WeekdayButton>
      )}
    />
  )
}

const useStyles = makeStyles()(theme => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.shape?.dayButton.size,
    minHeight: theme.shape?.dayButton.size,
    width: theme.shape?.dayButton.size,
    minWidth: theme.shape?.dayButton.size,
    borderRadius: '50%',
    margin: theme.spacing(0.5),
    [theme.breakpoints.down('md')]: {
      height: '36px',
      minHeight: '36px',
      width: '36px',
      minWidth: '36px',
      borderRadius: '50%',
      marginTop: 0,
    },
    [theme.breakpoints.between(0, 300)]: {
      height: '30px',
      minHeight: '30px',
      width: '30px',
      minWidth: '30px',
      borderRadius: '50%',
      marginTop: 0,
    },
    [theme.breakpoints.between('xs', 'xl')]: {
      marginBottom: theme.spacing(1),
    },
  },
  active: {
    backgroundColor: theme.palette.primary.main,
    color: '#ffffff',
    transition: 'all 0.3s ease-in-out',
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.grey.grey06,
        borderColor: theme.palette.grey.grey06,
        color: theme.palette.text.header,
      },
    },
  },
  inactive: {
    backgroundColor: theme.palette.grey.grey06,
    color: theme.palette.text.header,
    transition: 'all 0.3s ease-in-out',
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        color: '#ffffff',
      },
    },
  },
  disabled: {
    color: theme.palette.grey.grey03,
    opacity: 0.7,
  },
}))

interface WeekdayButtonProps {
  checked: boolean
  onChange: (newChecked: boolean) => void
  disabled?: boolean
  children: React.ReactNode
  error?: boolean
  isDirty?: boolean
}

/**
 * Round button. Used for weekdays. "Mon", "Tue"... as a controlled component.
 */
const WeekdayButton: React.FC<WeekdayButtonProps> = ({ checked, onChange, disabled, children, error, isDirty }) => {
  const { classes, cx } = useStyles()

  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const isInteractive = canEdit && !disabled

  const checkedColor = checked ? 'primary' : 'inherit'

  return (
    <Button
      className={cx(classes.button, checked ? classes.active : classes.inactive, !isInteractive && classes.disabled)}
      sx={{
        position: isDirty ? 'relative' : undefined,
        pointerEvents: isInteractive ? undefined : 'none',
      }}
      disableElevation
      variant="contained"
      color={error ? 'error' : checkedColor}
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      aria-disabled={disabled}
      onClick={disabled ? undefined : () => onChange(!checked)}
      disableRipple={disabled}
    >
      {children}
      {isDirty && (
        <Box
          sx={{
            position: 'absolute',
            top: -7,
            right: 1,
            color: theme => (checked ? theme.palette.primary.dark : theme.palette.primary.light),
          }}
        >
          <Typography variant={'h2'}>*</Typography>
        </Box>
      )}
    </Button>
  )
}
